<template>
    <div class="download-pdf">
        <div class="header">
            <div class="logo">
                <img src="/img/icons/pdf-icon.png" alt="MedMatch Logo" />
            </div>
        </div>
        <div class="title">
            <h2>{{ $t('modals.patient.medical.export_modal.report_title') }}</h2>
        </div>

        <div v-if="!appointments || appointments.length === 0" class="empty-notice">
            <p>{{ $t('modals.patient.medical.export_modal.no_appointments_timeline') }}</p>
        </div>

        <div v-else class="timeline-records">
            <div v-for="(step, index) in steps" :key="step.id || index" class="section">
                <h3 class="section-title">
                    #{{ index + 1 }} {{ getStepTitle(step) }} &mdash; {{ getStepDate(step) }} ({{ getStepTime(step) }})
                </h3>

                <table class="table">
                    <tbody>
                        <tr>
                            <td class="key">{{ $t('modals.patient.medical.export_modal.keys.status') }}</td>
                            <td class="value">
                                {{ getStepTag(step) || $t('modals.patient.medical.export_modal.na') }}
                            </td>
                        </tr>
                        <tr>
                            <td class="key">{{ $t('modals.patient.medical.export_modal.keys.provider') }}</td>
                            <td class="value">
                                {{ getStepProvider(step) || $t('modals.patient.medical.export_modal.na') }}
                            </td>
                        </tr>
                        <tr v-if="getStepSpeciality(step.referral?.fromUser)">
                            <td class="key">{{ $t('modals.patient.medical.export_modal.keys.speciality') }}</td>
                            <td class="value">{{ getStepSpeciality(step.referral?.fromUser) }}</td>
                        </tr>
                        <tr v-if="getStepLocation(step)">
                            <td class="key">{{ $t('modals.patient.medical.export_modal.keys.location') }}</td>
                            <td class="value">{{ getStepLocation(step) }}</td>
                        </tr>
                        <tr v-if="step.referral?.diagnosis">
                            <td class="key">{{ $t('modals.patient.medical.export_modal.keys.diagnosis') }}</td>
                            <td class="value">{{ step.referral?.diagnosis }}</td>
                        </tr>
                        <tr v-if="step.referral?.insurancePlan?.name">
                            <td class="key">{{ $t('modals.patient.medical.export_modal.keys.insurance_plan') }}</td>
                            <td class="value">{{ step.referral?.insurancePlan?.name }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="print-button">
            <common-button
                :label="$t('modals.patient.medical.export_modal.download_pdf')"
                type="primary"
                @click="printTimeline"
            ></common-button>
        </div>
    </div>
</template>

<script>
    import ReferralTrackingTypeEnum from '@/enums/ReferralTrackingTypeEnum'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'

    export default {
        props: {
            appointments: {
                type: Array,
                default: () => [],
            },
        },
        computed: {
            steps() {
                return (this.appointments || []).map((referral) => ({ id: referral.id, referral }))
            },
        },
        methods: {
            formatDate(date, includeTime = false) {
                if (!date) return 'N/A'
                const d = new Date(date)
                if (isNaN(d.getTime())) return date
                const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                if (!includeTime) return dateStr
                const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                return `${dateStr} ${timeStr}`
            },
            getStepDate(step) {
                return this.formatDate(step.referral?.appointmentDateTime || step.referral?.createdDateTime)
            },
            getStepTime(step) {
                return this.formatDate(step.referral?.appointmentDateTime || step.referral?.createdDateTime, true)
            },
            getStepTitle(step) {
                if (step?.referral?.followUpReferral) {
                    return this.$t('modals.patient.medical.steps.followUp')
                }
                const type = this.getStepType(step?.referral?.sourceType)
                return `${type} ${this.$t('modals.patient.medical.steps.referral')}`
            },
            getStepType(type) {
                if (type === ReferralTrackingTypeEnum.DIRECT) return this.$t('pages.referrals.index.referrals_made.type_direct')
                if (type === ReferralTrackingTypeEnum.MATCH) return this.$t('pages.referrals.index.referrals_made.type_matched')
                return 'N/A'
            },
            getStepTag(step) {
                const displayStatus = this.getDisplayStatus(step?.referral?.status, step?.referral?.sourceType)
                return this.$t(`pages.referrals.tracking.status_title.${displayStatus}`)
            },
            getDisplayStatus(status, sourceType) {
                const statusMapping = {
                    [ReferralTrackingTypeEnum.MATCH]: {
                        [ReferralStatusEnum.CREATED]: 'CREATED',
                        [ReferralStatusEnum.TIME_SLOTS_PROPOSED]: 'AWAITING_CONFIRMATION',
                        [ReferralStatusEnum.DECLINED]: 'DECLINED',
                        [ReferralStatusEnum.SCHEDULED]: 'SCHEDULED',
                        [ReferralStatusEnum.INACTIVE]: 'EXPIRED',
                    },
                    [ReferralTrackingTypeEnum.DIRECT]: {
                        [ReferralStatusEnum.PATIENT_VISIT_PENDING]: 'SCHEDULED',
                        [ReferralStatusEnum.PATIENT_VISIT_PAST]: 'NO_SHOW',
                        [ReferralStatusEnum.NO_SHOW]: 'NO_SHOW',
                        [ReferralStatusEnum.CANCELLED]: 'CANCELLED',
                        [ReferralStatusEnum.CONSULTATION_NOTES_PENDING]: 'CONSULTATION_NOTES_PENDING',
                        [ReferralStatusEnum.COMPLETED]: 'COMPLETED',
                    },
                }
                return statusMapping[sourceType]?.[status] || status
            },
            getStepSpeciality(user) {
                if (!user || !user.specialities || user.specialities.length === 0) return ''
                return user.specialities[0]?.speciality?.name || ''
            },
            getStepProvider(step) {
                const provider = step.referral?.toUser || step.referral?.fromUser
                if (!provider) return ''
                const fullName = [provider.firstName, provider.lastName].filter(Boolean).join(' ')
                const designation = provider.designation || ''
                return [fullName, designation].filter(Boolean).join(', ')
            },
            getStepLocation(step) {
                const location = step.referral?.practiceLocation
                return location && [location.name, location.address, location.state, location.zip].filter(Boolean).join(', ')
                    ? [location.name, location.address, location.state, location.zip].filter(Boolean).join(', ')
                    : ''
            },
            printTimeline() {
                const content = this.$el.innerHTML
                const printWindow = window.open('', '_blank', 'width=900,height=700')
                if (!printWindow) {
                    alert(this.$t('modals.patient.medical.export_modal.popup_warning'))
                    return
                }

                printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8" />
                        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                        <title>Medical_Timeline_Report</title>
                        <style>
                            * { box-sizing: border-box; }
                            body { background: white; margin: 0; padding: 20px; font-family: Arial, sans-serif; color: #333; }
                            .download-pdf { background: white; padding: 40px; max-width: 800px; margin: 0 auto; }
                            .header { text-align: center; margin-bottom: 20px; }
                            .logo img { width: 140px; }
                            .title { text-align: center; margin-bottom: 30px; }
                            .section { margin-bottom: 30px; page-break-inside: avoid; }
                            .section-title { text-align: left; font-size: 16px; margin-bottom: 10px; border-bottom: 2px solid #e5e7eb; padding-bottom: 6px; color: #111827; }
                            .table { width: 100%; border-collapse: collapse; font-size: 13px; }
                            .table th, .table td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; }
                            .key { width: 30%; font-weight: 600; background: #fafafa; }
                            .value { width: 70%; }
                            .print-button { display: none !important; }
                            @page { size: A4; margin: 1cm; }
                            @media print { body { padding: 0; } .download-pdf { padding: 0; max-width: 100%; } }
                        </style>
                    </head>
                    <body>
                        ${content}
                    </body>
                    </html>
                `)
                printWindow.document.close()
                printWindow.onload = () => {
                    setTimeout(() => {
                        printWindow.focus()
                        printWindow.print()
                    }, 300)
                }
                this.$emit('download-pdf')
            },
        },
    }
</script>

<style lang="css" scoped>
    .download-pdf {
        background: white;
        padding: 40px;
        max-width: 800px;
        margin: 0 auto;
        font-family: Arial, sans-serif;
        color: #333;
    }
    .header {
        text-align: center;
        margin-bottom: 20px;
    }
    .logo img {
        width: 140px;
    }
    .title {
        text-align: center;
        margin-bottom: 30px;
    }
    .empty-notice {
        text-align: center;
        color: #6b7280;
        padding: 40px 0;
    }
    .section {
        margin-bottom: 30px;
    }
    .section-title {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 10px;
        border-bottom: 2px solid #e5e7eb;
        padding-bottom: 6px;
        color: #111827;
    }
    .table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }
    .table td {
        border: 1px solid #ddd;
        padding: 8px 10px;
    }
    .key {
        width: 30%;
        font-weight: 600;
        background: #fafafa;
    }
    .value {
        width: 70%;
    }
    .print-button {
        display: flex;
        justify-content: flex-end;
        margin-top: 30px;
    }
</style>
